// src/utils/sessionUtils.js
import { endTrackingSession } from "./userEventsLogger";
// Durées (source of truth):
// - Durée max d'une session: 2 heures
// - Intervalle entre heartbeats: 1 minute (ne pas changer la logique d'envoi)
// - Délai d'inactivité après cap pour clôturer: 10 minutes
// PRODUCTION VALUES:
// - Cap: 2 hours
// - Heartbeat: 1 minute
// - Grace (post-cap inactivity): 10 minutes
const MAX_SESSION_DURATION = 4 * 60 * 60 * 1000; // 2h
export const HEARTBEAT_INTERVAL = 1 * 60 * 1000; // 1 min
const HEARTBEAT_TIMEOUT = 10 * 60 * 1000; // 10 min

/**
 * Créer une nouvelle session avec structure heartbeat
 */
export function createNewSession(userId) {
  const now = new Date().toISOString();
  return {
    sessionId: require("uuid").v4(),
    backendId: null, // MongoDB _id after first sync to backend
    userId: userId,
    startedAt: now,
    endedAt: null,
    heartbeats: [now], // Premier heartbeat au moment de la connexion
    activity_duration: 0, // Calculé à partir des heartbeats
    inactivity_duration: 0, // Calculé à partir des heartbeats
    sessionDurationReached: false,
  };
}

/**
 * Ajouter un heartbeat à la session courante
 */
export function addHeartbeat() {
  const session = getCurrentSession();
  if (session && !session.endedAt) {
    const now = new Date().toISOString();
    session.heartbeats.push(now);
    updateSessionStats(session);
    saveCurrentSession(session);
    console.log("Heartbeat envoyé:", now);
    return true;
  }
  return false;
}

/**
 * Calculer les statistiques d'activité/inactivité basées sur les heartbeats
 */
function updateSessionStats(session) {
  if (!session.heartbeats || session.heartbeats.length === 0) return;

  const HEARTBEAT_EXPECTED_INTERVAL = 1;
  const HEARTBEAT_TOLERANCE = 0.5;

  let activityTime = 0;
  let inactivityTime = 0;

  // Calculer l'activité/inactivité basée sur les intervalles entre heartbeats
  for (let i = 0; i < session.heartbeats.length - 1; i++) {
    const currentHeartbeat = new Date(session.heartbeats[i]).getTime();
    const nextHeartbeat = new Date(session.heartbeats[i + 1]).getTime();
    const intervalMinutes = (nextHeartbeat - currentHeartbeat) / (60 * 1000);

    if (intervalMinutes <= HEARTBEAT_EXPECTED_INTERVAL + HEARTBEAT_TOLERANCE) {
      activityTime += intervalMinutes;
    } else {
      activityTime += HEARTBEAT_EXPECTED_INTERVAL;
      inactivityTime += intervalMinutes - HEARTBEAT_EXPECTED_INTERVAL;
    }
  }

  // Gérer le temps depuis le dernier heartbeat
  if (session.heartbeats.length > 0) {
    const lastHeartbeat = new Date(
      session.heartbeats[session.heartbeats.length - 1]
    ).getTime();
    const now = session.endedAt
      ? new Date(session.endedAt).getTime()
      : Date.now();
    const timeSinceLastHeartbeat = (now - lastHeartbeat) / (60 * 1000);

    if (
      timeSinceLastHeartbeat <=
      HEARTBEAT_EXPECTED_INTERVAL + HEARTBEAT_TOLERANCE
    ) {
      activityTime += timeSinceLastHeartbeat;
    } else {
      activityTime += HEARTBEAT_EXPECTED_INTERVAL;
      inactivityTime += timeSinceLastHeartbeat - HEARTBEAT_EXPECTED_INTERVAL;
    }
  }

  // CORRECTION: Arrondir à l'entier le plus proche au lieu de 1 décimale
  session.activity_duration = Math.round(activityTime);
  session.inactivity_duration = Math.round(inactivityTime);
}

/**
 * Vérifier si la session courante doit être fermée (durée max atteinte)
 */
export function checkSessionTimeout() {
  const session = getCurrentSession();
  if (!session || session.endedAt) return null;

  // Mettre à jour les stats avant décision pour aligner activity/inactivity
  try {
    updateSessionStats(session);
    saveCurrentSession(session);
  } catch {}

  const startTime = new Date(session.startedAt).getTime();
  const now = Date.now();

  // Vérifier si la durée max est atteinte
  if (now - startTime >= MAX_SESSION_DURATION) {
    const lastHeartbeat = session.heartbeats[session.heartbeats.length - 1];
    const lastHeartbeatTime = new Date(lastHeartbeat).getTime();
    const timeSinceLastHeartbeat = now - lastHeartbeatTime;

    // Si l'utilisateur est encore actif (dernier heartbeat < 10 min)
    if (timeSinceLastHeartbeat < HEARTBEAT_TIMEOUT) {
      return "EXTEND_SESSION"; // Prolonger jusqu'à inactivité
    } else {
      return "END_SESSION"; // Terminer immédiatement
    }
  }

  return null;
}

/**
 * Terminer la session courante avec raison
 */
export function endCurrentSession(reason = "manual") {
  const session = getCurrentSession();
  if (session && !session.endedAt) {
    // Archiver le tracking outil avant de supprimer la session
    try {
      endTrackingSession();
    } catch (e) {
      console.warn("Erreur lors de l'archivage du tracking outil:", e);
    }
    updateSessionStats(session);
    session.endedAt = new Date().toISOString();
    session.endReason = reason;

    // Sauvegarder dans la liste des sessions
    const sessions = JSON.parse(localStorage.getItem("userSessions") || "[]");
    sessions.push(session);
    localStorage.setItem("userSessions", JSON.stringify(sessions));

    // Supprimer la session courante
    localStorage.removeItem("currentUserSession");

    console.log("Session terminée:", reason);
    return session;
  }
  return null;
}

/**
 * Fonctions utilitaires
 */
export function getCurrentSession() {
  const session = localStorage.getItem("currentUserSession");
  return session ? JSON.parse(session) : null;
}

export function saveCurrentSession(session) {
  localStorage.setItem("currentUserSession", JSON.stringify(session));
}

/**
 * Nettoyer les sessions orphelines (ancien système de compatibilité)
 */
export function fixOrphanedSessions() {
  const sessions = JSON.parse(localStorage.getItem("userSessions") || "[]");
  let currentSession = localStorage.getItem("currentUserSession");
  let changed = false;

  // Vérifier la session courante
  if (currentSession) {
    currentSession = JSON.parse(currentSession);

    if (!currentSession.endedAt) {
      const startTime = new Date(currentSession.startedAt).getTime();
      const now = Date.now();
      const lastHeartbeat =
        currentSession.heartbeats?.[currentSession.heartbeats.length - 1];
      const lastHeartbeatTime = lastHeartbeat
        ? new Date(lastHeartbeat).getTime()
        : startTime;
      const postCapGraceCutoff =
        startTime + MAX_SESSION_DURATION + HEARTBEAT_TIMEOUT;

      // Cas 1: cap dépassé ET inactivité >= HEARTBEAT_TIMEOUT -> clôture immédiate
      if (
        now - startTime >= MAX_SESSION_DURATION &&
        now - lastHeartbeatTime >= HEARTBEAT_TIMEOUT
      ) {
        updateSessionStats(currentSession);
        currentSession.endedAt = new Date().toISOString();
        currentSession.endReason = "max_duration_orphaned";
        sessions.push(currentSession);
        localStorage.removeItem("currentUserSession");
        changed = true;
      } else if (now > postCapGraceCutoff) {
        // Filet de sécurité: jamais dépasser cap + grâce (10min) + buffer
        updateSessionStats(currentSession);
        currentSession.endedAt = new Date(postCapGraceCutoff).toISOString();
        currentSession.endReason = "orphaned_timeout";
        sessions.push(currentSession);
        localStorage.removeItem("currentUserSession");
        changed = true;
      }
    }
  }

  if (changed) {
    localStorage.setItem("userSessions", JSON.stringify(sessions));
  }
}

/**
 * Termine proprement la session utilisateur courante :
 * - Ajoute la session courante à la liste userSessions (localStorage)
 * - Ajoute un champ endedAt (date ISO)
 * - Supprime currentUserSession du localStorage
 * À appeler juste avant le logout manuel.
 */
export function endCurrentSessionManually() {
  return endCurrentSession("manual");
}
